import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "gatsby";
import { AnchorLink } from "gatsby-plugin-anchor-links";
import ExternalIcon from "../images/external.inline.svg";
import { modifyColor, addAlpha, checkDarkMode } from "../utils/helpers";

const StyledExternal = styled.a``;
const StyledLink = styled(Link)`
  img {
    margin-right: 5px;
  }
`;
const StyledAnchor = styled(AnchorLink)``;
const StyledButton = styled.button`
  &:active:focus {
    outline: none;
  }
`;
const ButtonBase = styled(
  ({ to, target, rel, children, dangerouslySetInnerHTML, ...props }) =>
    to === undefined ? (
      <StyledButton
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        {...props}
      >
        {children}
      </StyledButton>
    ) : target ? (
      <StyledExternal href={to} target={target} rel={rel} {...props}>
        {children ? (
          children
        ) : (
          <span dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}{" "}
        <ExternalIcon />
      </StyledExternal>
    ) : to?.includes("#") ? (
      <StyledAnchor to={to} {...props}>
        {children ? (
          children
        ) : (
          <span dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}
      </StyledAnchor>
    ) : (
      <StyledLink
        to={to}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        {...props}
      >
        {children}
      </StyledLink>
    )
)`
  color: var(--text-color);
  background-color: var(--bg-color);
  border: 1px solid var(--border-color);
  font-size: 1rem;
  border-radius: var(--border-radius);
  line-height: initial;
  text-decoration: none;
  transition: 0.3s all ease-in-out;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 64px;

  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      cursor: pointer;
      background-color: var(--bg-color--hover);
      border-color: var(--border-color--hover, var(--bg-color--hover));
      color: var(--text-color--hover, var(--text-color, currentColor));
    }
  }

  &:active {
    background-color: var(--bg-color--hover);
  }
  &.disabled,
  &:disabled {
    background-color: var(--bg-color--disabled);
    border-color: var(--border-color--disabled, var(--bg-color--disabled));
    color: var(--text-color--disabled, var(--text-color, currentColor));
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.6;
    @media (hover: hover) {
      &:hover {
        background-color: var(--bg-color--disabled);
        color: var(--text-color--disabled, var(--text-color, currentColor));
        border-color: var(--border-color--disabled, var(--bg-color--disabled));
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    margin-left: 0;
  }
`;
const Filled = styled(
  ({
    backgroundColor: backgroundColorIgnored,
    textColor: textColorIgnored,
    ...rest
  }) => <ButtonBase {...rest} />
)`
  ${({ backgroundColor, textColor, theme }) => {
    const baseBg =
      checkDarkMode(backgroundColor, theme) || theme.colors.primary;
    const baseText = checkDarkMode(textColor, theme) || theme.colors.white;

    return `
      --bg-color: ${baseBg};
      --bg-color--hover: ${modifyColor(baseBg, 40)};
      --text-color: ${baseText};
      --border-color: ${baseBg};
      --bg-color--disabled: ${modifyColor(baseBg, 40)};
    `;
  }}
`;

const Outlined = styled(
  ({
    backgroundColor: backgroundColorIgnored,
    textColor: textColorIgnored,
    ...rest
  }) => <ButtonBase {...rest} />
)`
  ${({ textColor, backgroundColor, theme }) => {
    const baseColor =
      checkDarkMode(textColor || backgroundColor, theme) ||
      theme.colors.primary;
    const disabledColor = modifyColor(baseColor, 70);
    const hoverColor = addAlpha(baseColor, 0.3);

    return `
      --border-color: ${baseColor};
      --bg-color: transparent;
      --text-color: ${baseColor};
      --bg-color--hover: ${hoverColor};
      --border-color--hover: ${baseColor};
      --text-color--hover: ${baseColor};
      --text-color--disabled: ${disabledColor};
      --border-color--disabled: ${disabledColor};
    `;
  }}
`;

export default function Button({
  url,
  variant,
  label,
  className,
  innerClassName,
  target,
  rel,
  backgroundColor,
  textColor,
  disabled,
  children,
  onClick,
  ...props
}) {
  const Button = variant === "filled" ? Filled : Outlined;
  return (
    <Button
      aria-label={label}
      to={url}
      target={target}
      rel={rel}
      className={
        disabled
          ? "disabled"
          : `${className ?? ""} ${innerClassName ?? ""}`.trim()
      }
      backgroundColor={backgroundColor}
      textColor={textColor}
      onClick={onClick}
      {...{
        ...props,
        ...(children
          ? { children }
          : { dangerouslySetInnerHTML: { __html: label } }),
      }}
    />
  );
}

Button.propTypes = {
  url: PropTypes.string,
  variant: PropTypes.oneOf(["filled", "outlined"]),
  label: PropTypes.string,
  className: PropTypes.string,
  innerClassName: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
};

Button.defaultProps = {
  innerClassName: undefined,
  variant: "filled",
};
